import React from 'react';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Fade from '@mui/material/Fade';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import BusinessIcon from '@mui/icons-material/Business';
import EngineeringIcon from '@mui/icons-material/Engineering';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';

const navLinks = ['Home', 'Services', 'Projects', 'Certifications', 'About', 'Contact'];

const Footer = () => {
  const theme = useTheme();
  
  const contactInfo = [
    { icon: <LocationOnIcon sx={{ fontSize: 20 }} />, text: '1906 Stewart Ave, New Hyde Park, NY 11040' },
    { icon: <PhoneIcon sx={{ fontSize: 20 }} />, text: '(516) 414-0165 • Fax: (516) 414-0166' },
    { icon: <EmailIcon sx={{ fontSize: 20 }} />, text: 'anil@anrmechanical.com' },
  ];

  return (
    <>
      <Box 
        component="footer" 
        sx={{ 
          background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.grey[900]})`,
          color: 'rgba(255,255,255,0.9)',
          py: 8,
          mt: 8,
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '1px',
            background: `linear-gradient(90deg, transparent, ${theme.palette.secondary.main}, transparent)`,
          }
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 4, md: 6 }}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Fade in timeout={1000}>
                <Box>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                    <BusinessIcon sx={{ color: theme.palette.secondary.main, fontSize: 32 }} />
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'white' }}>
                      ANR Mechanical Corp.
                    </Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ mb: 3, lineHeight: 1.7 }}>
                    Established in 1998, ANR Mechanical Corp. delivers full-service mechanical solutions across the NY metro area. Full-service mechanical contractor specializing in HVAC design-build, piping, and critical environments. Licensed • Bonded • Insured.
                  </Typography>
                </Box>
              </Fade>
            </Grid>
            
            <Grid size={{ xs: 12, md: 4 }}>
              <Fade in timeout={1200}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: 'white' }}>
                    Contact Information
                  </Typography>
                  <Stack spacing={2}>
                    {contactInfo.map((item, index) => (
                      <Stack key={index} direction="row" spacing={2} alignItems="flex-start">
                        <Box sx={{ color: theme.palette.secondary.main, mt: 0.5 }}>
                          {item.icon}
                        </Box>
                        <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                          {item.text}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Box>
              </Fade>
            </Grid>
            
            <Grid size={{ xs: 12, md: 4 }}>
              <Fade in timeout={1400}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: 'white' }}>
                    Quick Links
                  </Typography>
                  <Grid container spacing={1}>
                    {navLinks.map((link, index) => (
                      <Grid size={{ xs: 6 }} key={link}>
                        <Link 
                          href={`#${link.toLowerCase()}`} 
                          variant="body2" 
                          underline="none" 
                          sx={{ 
                            color: 'rgba(255,255,255,0.8)',
                            display: 'block',
                            py: 0.5,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              color: theme.palette.secondary.main,
                              transform: 'translateX(4px)',
                            }
                          }}
                        >
                          {link}
                        </Link>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Fade>
            </Grid>
          </Grid>
        </Container>
      </Box>
      
      {/* Professional Copyright Section */}
      <Box sx={{
        borderTop: 1,
        borderColor: 'rgba(255, 255, 255, 0.1)',
        py: 4,
        backgroundColor: '#000000'
      }}>
        <Container maxWidth="lg">
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between', 
            alignItems: 'center',
            gap: 3,
            textAlign: { xs: 'center', md: 'left' }
          }}>
            {/* Copyright and Licensing */}
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'center', md: 'flex-start' },
              gap: { xs: 2, md: 4 },
              flex: 1
            }}>
              <Box>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.92)', fontWeight: 600 }}>
                  © 2025 ANR Mechanical Corp. All rights reserved.
                </Typography>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  justifyContent: { xs: 'center', md: 'flex-start' }
                }}>
                  <BusinessIcon sx={{ color: 'secondary.main', fontSize: 16 }} />
                  <Typography variant="caption" sx={{ color: theme.palette.secondary.main, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                    Licensed • Bonded • Insured
                  </Typography>
                </Box>
              </Box>
            </Box>
            
            {/* Developed By Section */}
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: { xs: 'center', md: 'flex-end' },
              gap: 1
            }}>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', textTransform: 'uppercase', letterSpacing: 1 }}>
                Designed & Developed by
              </Typography>
              <Link
                href="https://www.codingbullz.com/"
                target="_blank"
                rel="noopener"
                title="CodingBull Technovations Pvt Ltd"
                aria-label="CodingBull Technovations Pvt Ltd"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1.25,
                  textDecoration: 'none',
                  '&:hover .cb-name': { color: '#5FC4EC' }
                }}
              >
                <Box
                  component="img"
                  src="/brand/codingbull-logo.png"
                  alt="CodingBull Technovations Pvt Ltd logo"
                  sx={{ height: 46, width: 46, display: 'block', flexShrink: 0 }}
                />
                <Typography
                  className="cb-name"
                  component="span"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.95)',
                    fontWeight: 700,
                    fontSize: '0.9rem',
                    lineHeight: 1.25,
                    transition: 'color 0.3s ease'
                  }}
                >
                  CodingBull Technovations Pvt&nbsp;Ltd
                </Typography>
              </Link>
            </Box>
          </Box>
          
          {/* Additional Professional Certifications */}
          <Box sx={{ 
            mt: 3, 
            pt: 3, 
            borderTop: 1, 
            borderColor: 'rgba(255, 255, 255, 0.15)',
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: 4
          }}>
            {[
              { icon: <EngineeringIcon />, text: 'Professional Engineers' },
              { icon: <WorkspacePremiumIcon />, text: 'Certified Contractors' }
            ].map((cert, index) => (
              <Box key={index} sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1.5,
                color: 'rgba(255, 255, 255, 0.85)'
              }}>
                {React.cloneElement(cert.icon, { sx: { fontSize: 18, color: 'secondary.main' } })}
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {cert.text}
                </Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default Footer;
